import { formatDateTimeDDMMYY } from '../../utils/dateFormat';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';

import {
  AdminCancellationImpact,
  AdminIncomeStreamBreakdown,
  AdminIncomeStreamOption,
  AdminTransaction,
  AdminTransactionMutationPayload,
  AdminTransactionSummary,
  createFinancialTransaction,
  deleteFinancialTransaction,
  getGlobalTransactions,
  sendInvoiceToClientAdmin,
  updateFinancialTransaction,
} from '../../services/adminService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useAuth } from '../../hooks/useAuth';
import { formatTransactionDescription, prettyResourceType } from '../../utils/formatTransaction';

type StatusFilter = 'all' | 'paid' | 'pending' | 'failed' | 'refunded' | 'voided';
type LedgerTab = 'ledger' | 'queues';
type DateRangeFilter = 'all' | 'today' | 'yesterday' | 'last7' | 'this_month' | 'last_month' | 'this_quarter';

const cardSx = { borderRadius: 0, border: '0.5px solid', borderColor: 'divider', bgcolor: 'background.paper' } as const;
const labelSx = { fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: 'text.disabled' };
const monoSx = { fontFamily: "'DM Mono', monospace", fontSize: '0.76rem', fontWeight: 800 };

const defaultSummary: AdminTransactionSummary = { totalRevenue: 0, pendingRevenue: 0, failedRevenue: 0, completedCount: 0, pendingCount: 0, failedCount: 0, count: 0 };
const fallbackIncomeStreams: AdminIncomeStreamOption[] = [
  { key: 'mixed', label: 'Mixed store purchase' },
  { key: 'account', label: 'Account / renewal' },
  { key: 'channel', label: 'Channels' },
  { key: 'playkaster', label: 'PlayKaster' },
  { key: 'hls', label: 'HLS' },
  { key: 'hls_plus', label: 'HLS+' },
  { key: 'direct', label: 'Direct Pass' },
  { key: 'managed_service', label: 'Managed Service' },
  { key: 'remapped', label: 'Remapped Output' },
  { key: 'social', label: 'Social Stream' },
  { key: 'epg', label: 'EPG' },
  { key: 'manual', label: 'Manual admin' },
  { key: 'other', label: 'Other' },
];
const dateRangeOptions: Array<{ value: DateRangeFilter; label: string }> = [
  { value: 'all', label: 'All dates' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
];

const blankForm: AdminTransactionMutationPayload = {
  userIdentifier: '',
  amount: 0,
  currency: 'USD',
  status: 'PENDING',
  description: '',
  gatewayTxId: '',
  gateway: 'MANUAL',
  paymentType: '',
  paymentAccount: '',
  paymentDateTime: '',
  incomeStream: 'manual',
  quantity: 1,
  processed: false,
  effectApplied: false,
  reason: '',
};

const localDateTimeInput = (value: Date = new Date()) => {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const ledgerPaymentTypeOptions = [
  'MPESA DIRECT',
  'MPESA TILL',
  'AIRTEL MONEY',
  'PESALINK',
  'PESAPAL / CARD',
  'EFT / SWIFT',
  'DIGITAL / CRYPTO',
];

const isPaid = (status: string) => ['paid', 'completed'].includes(String(status).toLowerCase());
const isPending = (status: string) => ['pending', 'processing'].includes(String(status).toLowerCase());
const isFailed = (status: string) => String(status).toLowerCase() === 'failed';
const isVoided = (status: string) => ['voided', 'cancelled', 'canceled'].includes(String(status).toLowerCase());
const isInvoiceRecord = (tx: AdminTransaction) => tx.recordType === 'INVOICE' || tx.metadata?.pendingInvoice === true;
const invoiceReferenceForTransaction = (tx?: AdminTransaction | null) => tx?.invoiceId
  || tx?.invoiceNumber
  || tx?.metadata?.goInvoiceId
  || (Array.isArray(tx?.metadata?.goInvoiceIds) ? tx?.metadata?.goInvoiceIds[0] : null)
  || tx?.metadata?.checkout?.goInvoiceId
  || (Array.isArray(tx?.metadata?.checkout?.goInvoiceIds) ? tx?.metadata?.checkout?.goInvoiceIds[0] : null)
  || null;
const isProvisionable = (tx: AdminTransaction) => isPaid(tx.status) && ['mixed', 'channel', 'playkaster', 'hls', 'hls_plus', 'direct', 'managed_service', 'remapped', 'social', 'epg'].includes(String(tx.incomeStream || ''));
const isStorePurchase = (tx: AdminTransaction | null) => {
  const metadata = tx?.metadata || {};
  const action = String(metadata.action || metadata.intent?.metadata?.action || '').toUpperCase();
  const items = metadata.billableItems || metadata.intent?.metadata?.billableItems || metadata.items;
  return ['ADD_RESOURCE', 'BATCH_ADD_RESOURCE'].includes(action) && Array.isArray(items) && items.length > 0;
};
const money = (amount: number, currency = 'USD') => `${currency} ${Number(amount || 0).toLocaleString()}`;
const ADMIN_LEDGER_CURRENCY_KEY = 'streamy.admin.displayCurrency';
const countryCurrency = (country?: string | null) => {
  const value = String(country || '').trim().toLowerCase();
  if (['kenya', 'ke'].includes(value)) return 'KES';
  if (['tanzania', 'tz'].includes(value)) return 'TZS';
  if (['uganda', 'ug'].includes(value)) return 'UGX';
  if (['south africa', 'za'].includes(value)) return 'ZAR';
  if (['nigeria', 'ng'].includes(value)) return 'NGN';
  if (['united kingdom', 'uk', 'gb'].includes(value)) return 'GBP';
  if (['germany', 'france', 'italy', 'spain', 'netherlands', 'ireland', 'eu'].includes(value)) return 'EUR';
  return 'USD';
};
const dateLabel = (value?: string | null) => {
  return formatDateTimeDDMMYY(value);
};

const StatusChip = ({ status }: { status: string }) => {
  const normalized = String(status).toLowerCase();
  let color: 'success' | 'warning' | 'error' | 'default' = 'default';
  let icon = <ReceiptLongIcon sx={{ fontSize: 14 }} />;
  if (isPaid(status)) { color = 'success'; icon = <CheckCircleIcon sx={{ fontSize: 14 }} />; }
  else if (isPending(status)) { color = 'warning'; icon = <HourglassEmptyIcon sx={{ fontSize: 14 }} />; }
  else if (isFailed(status)) { color = 'error'; icon = <ErrorIcon sx={{ fontSize: 14 }} />; }
  else if (isVoided(status)) { color = 'default'; icon = <ClearIcon sx={{ fontSize: 14 }} />; }
  return <Chip label={normalized.toUpperCase()} color={color} icon={icon} size="small" sx={{ borderRadius: 0, fontSize: '0.66rem', fontWeight: 900, height: 22 }} />;
};

const FinanceMetric = ({ label, value, helper, color, onClick }: { label: string; value: React.ReactNode; helper?: string; color: string; onClick?: () => void }) => (
  <Box onClick={onClick} sx={{ minWidth: 132, borderLeft: `3px solid ${color}`, pl: 1.25, cursor: onClick ? 'pointer' : 'default' }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: '1.14rem', fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
    {helper && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{helper}</Typography>}
  </Box>
);

const QueueRow = ({ tx, onOpen }: { tx: AdminTransaction; onOpen?: () => void }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 1.15, borderLeft: '3px solid', borderLeftColor: isFailed(tx.status) ? 'error.main' : isPending(tx.status) ? 'warning.main' : 'success.main' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900 }}>{tx.userEmail}</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }} noWrap>{tx.incomeStreamLabel || 'Other'} / {tx.description || 'Transaction'}</Typography>
        <Typography sx={{ ...monoSx, color: 'text.disabled' }}>User {tx.userHid || tx.userId} / {dateLabel(tx.date)}</Typography>
      </Box>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography sx={monoSx}>{money(tx.amount, tx.currency)}</Typography>
        <StatusChip status={tx.status} />
        {onOpen && <Button size="small" variant="outlined" onClick={onOpen} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Filter</Button>}
      </Stack>
    </Stack>
  </Paper>
);

export const BillingAdminPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();
  const defaultCurrency = useMemo(() => countryCurrency(user?.country), [user?.country]);
  const [activeTab, setActiveTab] = useState<LedgerTab>('ledger');
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>('all');
  const [displayCurrency, setDisplayCurrency] = useState(() => localStorage.getItem(ADMIN_LEDGER_CURRENCY_KEY) || '');
  const [summary, setSummary] = useState<AdminTransactionSummary>(defaultSummary);
  const [sourceBreakdown, setSourceBreakdown] = useState<AdminIncomeStreamBreakdown[]>([]);
  const [cancellationImpact, setCancellationImpact] = useState<AdminCancellationImpact | null>(null);
  const [incomeStreams, setIncomeStreams] = useState<AdminIncomeStreamOption[]>(fallbackIncomeStreams);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTx, setEditingTx] = useState<AdminTransaction | null>(null);
  const [form, setForm] = useState<AdminTransactionMutationPayload>(blankForm);
  const [deleteTarget, setDeleteTarget] = useState<AdminTransaction | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [invoiceSendTarget, setInvoiceSendTarget] = useState<AdminTransaction | null>(null);
  const [invoiceSendReason, setInvoiceSendReason] = useState('');
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<number[]>([]);

  const fetchTx = async () => {
    setLoading(true);
    try {
      const data = await getGlobalTransactions(page, pageSize, { status: statusFilter, source: sourceFilter, search: searchTerm, currency: displayCurrency || defaultCurrency, dateRange: dateRangeFilter });
      setTransactions(data.payments || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setSummary(data.summary || defaultSummary);
      setSourceBreakdown(data.sourceBreakdown || []);
      setCancellationImpact(data.cancellationImpact || null);
      setIncomeStreams(data.incomeStreams?.length ? data.incomeStreams : fallbackIncomeStreams);
    } catch {
      showSnackbar('Failed to load financial ledger.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!displayCurrency && defaultCurrency) setDisplayCurrency(defaultCurrency);
  }, [defaultCurrency, displayCurrency]);

  useEffect(() => {
    if (displayCurrency) localStorage.setItem(ADMIN_LEDGER_CURRENCY_KEY, displayCurrency);
  }, [displayCurrency]);

  useEffect(() => {
    const handler = (event: Event) => {
      const next = (event as CustomEvent<{ currency?: string }>).detail?.currency;
      if (next) setDisplayCurrency(next);
    };
    window.addEventListener('streamy-admin-currency-change', handler);
    return () => window.removeEventListener('streamy-admin-currency-change', handler);
  }, []);

  useEffect(() => { fetchTx(); }, [page, pageSize, statusFilter, sourceFilter, dateRangeFilter, searchTerm, displayCurrency, defaultCurrency]);

  const visibleTransactionIds = useMemo(() => transactions.filter((tx) => !isInvoiceRecord(tx)).map((tx) => tx.id), [transactions]);
  const selectedVisibleCount = useMemo(() => visibleTransactionIds.filter((id) => selectedTransactionIds.includes(id)).length, [selectedTransactionIds, visibleTransactionIds]);
  const allVisibleSelected = visibleTransactionIds.length > 0 && selectedVisibleCount === visibleTransactionIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedTransactionIds((prev) => {
      const visible = new Set(visibleTransactionIds);
      if (!checked) return prev.filter((id) => !visible.has(id));
      return Array.from(new Set([...prev, ...visibleTransactionIds]));
    });
  };

  const toggleTransactionSelection = (id: number) => {
    setSelectedTransactionIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const resetToFirstPage = (fn: () => void) => { setPage(1); fn(); };
  const applySearch = () => resetToFirstPage(() => setSearchTerm(searchDraft.trim()));
  const clearFilters = () => {
    setPage(1);
    setSearchDraft('');
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setDateRangeFilter('all');
  };

  const openCreate = () => {
    setEditingTx(null);
    setForm({ ...blankForm, paymentDateTime: localDateTimeInput() });
    setFormOpen(true);
  };

  const openEdit = (tx: AdminTransaction) => {
    if (isInvoiceRecord(tx)) {
      showSnackbar('Invoice status is managed by StreamyBilling and changes when payment is recorded.', 'info');
      return;
    }
    setEditingTx(tx);
    setForm({
      amount: tx.originalAmount ?? tx.amount,
      currency: tx.originalCurrency || tx.currency || 'USD',
      status: String(tx.status || 'PENDING').toUpperCase(),
      description: tx.description || tx.displayDescription || '',
      displayDescription: tx.displayDescription || '',
      gatewayTxId: tx.gatewayTxId || '',
      gateway: 'MANUAL',
      paymentType: tx.metadata?.paymentType === 'MANUAL' ? '' : tx.metadata?.paymentType || (String(tx.gateway).toUpperCase() === 'PESAPAL' ? 'PESAPAL / CARD' : String(tx.gateway).toUpperCase() === 'MPESA' ? 'MPESA DIRECT' : String(tx.gateway).toUpperCase() === 'EFT' ? 'EFT / SWIFT' : ''),
      paymentAccount: tx.metadata?.paymentAccount || '',
      paymentDateTime: localDateTimeInput(new Date(tx.metadata?.paymentDateTime || tx.date || Date.now())),
      incomeStream: tx.incomeStream || 'other',
      resourceType: tx.resourceTypes?.[0] || '',
      quantity: 1,
      processed: Boolean(tx.processed),
      effectApplied: Boolean(tx.effectApplied),
      reason: '',
    });
    setFormOpen(true);
  };

  const saveForm = async () => {
    if (!form.reason?.trim()) {
      showSnackbar('Reason is required for ledger changes.', 'warning');
      return;
    }
    if (!form.gatewayTxId?.trim() || !form.paymentType || !form.paymentAccount?.trim() || !form.paymentDateTime || !form.gateway) {
      showSnackbar('Gateway, payment type, reference, account, and payment date/time are required.', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingTx) {
        const { gateway, ...editable } = form;
        await updateFinancialTransaction(editingTx.id, { ...editable, paymentDateTime: new Date(form.paymentDateTime as string).toISOString() });
      }
      else await createFinancialTransaction({ ...form, paymentDateTime: new Date(form.paymentDateTime as string).toISOString() });
      showSnackbar(editingTx ? 'Ledger entry updated.' : 'Ledger entry created.', 'success');
      setFormOpen(false);
      await fetchTx();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Ledger change failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deleteReason.trim()) {
      showSnackbar('Reason is required to delete ledger entries.', 'warning');
      return;
    }
    setSaving(true);
    try {
      await deleteFinancialTransaction(deleteTarget.id, deleteReason);
      showSnackbar('Ledger entry deleted.', 'success');
      setDeleteTarget(null);
      setDeleteReason('');
      await fetchTx();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Delete failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmInvoiceSend = async () => {
    const invoiceReference = invoiceReferenceForTransaction(invoiceSendTarget);
    if (!invoiceReference) {
      showSnackbar('This invoice does not have a valid billing reference.', 'error');
      return;
    }
    if (!invoiceSendReason.trim()) {
      showSnackbar('Reason is required to send an invoice manually.', 'warning');
      return;
    }
    setInvoiceSending(true);
    try {
      const result = await sendInvoiceToClientAdmin(String(invoiceReference), invoiceSendReason.trim());
      showSnackbar(result.message || `Invoice queued for ${result.recipient}.`, 'success');
      setInvoiceSendTarget(null);
      setInvoiceSendReason('');
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Invoice delivery failed.', 'error');
    } finally {
      setInvoiceSending(false);
    }
  };

  const failedQueue = transactions.filter((tx) => isFailed(tx.status)).slice(0, 8);
  const pendingQueue = transactions.filter((tx) => isPending(tx.status)).slice(0, 8);
  const provisioningQueue = transactions.filter(isProvisionable).slice(0, 10);
  const selectedSourceLabel = sourceFilter === 'all' ? 'All income streams' : (incomeStreams.find((item) => item.key === sourceFilter)?.label || sourceFilter);
  const editingStorePurchase = isStorePurchase(editingTx);

  return (
    <Box sx={{ maxWidth: 1800, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={2.3} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountBalanceWalletIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900 }}>Financial Ledger</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{selectedSourceLabel} / {total} matching transaction{total === 1 ? '' : 's'}</Typography>
              </Box>
            </Stack>
            <FinanceMetric label="Collected" value={money(summary.totalRevenue, displayCurrency || defaultCurrency)} helper={`${summary.completedCount} completed`} color={theme.palette.success.main} onClick={() => resetToFirstPage(() => setStatusFilter('paid'))} />
            <FinanceMetric label="Pending" value={money(summary.pendingRevenue, displayCurrency || defaultCurrency)} helper={`${summary.pendingCount} entries`} color={theme.palette.warning.main} onClick={() => resetToFirstPage(() => setStatusFilter('pending'))} />
            <FinanceMetric label="Failed" value={money(summary.failedRevenue, displayCurrency || defaultCurrency)} helper={`${summary.failedCount} failed`} color={summary.failedCount ? theme.palette.error.main : theme.palette.info.main} onClick={() => resetToFirstPage(() => setStatusFilter('failed'))} />
            <FinanceMetric label="Scheduled Loss" value={money(cancellationImpact?.totals.monthlyLostRevenue || 0, cancellationImpact?.currency || displayCurrency || defaultCurrency)} helper={`${cancellationImpact?.totals.count || 0} deletion(s)`} color={theme.palette.error.main} />
            <FinanceMetric label="Rows" value={summary.count} helper="filter result" color={theme.palette.secondary.main} />
          </Stack>
          <Stack direction="row" spacing={0.75} justifyContent="flex-end">
            <Tooltip title="Financial Reports">
              <IconButton size="small" onClick={() => navigate('/admin/billing/reports')} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider', width: 34, height: 34 }}>
                <AssessmentIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh ledger">
              <span><IconButton size="small" onClick={fetchTx} disabled={loading} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider', width: 34, height: 34 }}>{loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}</IconButton></span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <TextField fullWidth size="small" placeholder="Search email, hashid, invoice, payment reference, description, or resource type..." value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') applySearch(); }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, sx: { borderRadius: 0 } }} />
          <TextField select size="small" label="Income stream" value={sourceFilter} onChange={(e) => resetToFirstPage(() => setSourceFilter(e.target.value))} sx={{ minWidth: { xs: '100%', lg: 190 } }} InputProps={{ sx: { borderRadius: 0 } }}>
            <MenuItem value="all">All income streams</MenuItem>
            {incomeStreams.map((stream) => <MenuItem key={stream.key} value={stream.key}>{stream.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => resetToFirstPage(() => setStatusFilter(e.target.value as StatusFilter))} sx={{ minWidth: { xs: '100%', lg: 140 } }} InputProps={{ sx: { borderRadius: 0 } }}>
            <MenuItem value="all">All</MenuItem><MenuItem value="paid">Paid</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="failed">Failed</MenuItem><MenuItem value="refunded">Refunded</MenuItem>
            <MenuItem value="voided">Voided</MenuItem>
          </TextField>
          <TextField select size="small" label="Duration" value={dateRangeFilter} onChange={(e) => resetToFirstPage(() => setDateRangeFilter(e.target.value as DateRangeFilter))} sx={{ minWidth: { xs: '100%', lg: 150 } }} InputProps={{ sx: { borderRadius: 0 } }}>
            {dateRangeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Rows" value={pageSize} onChange={(e) => resetToFirstPage(() => setPageSize(Number(e.target.value)))} sx={{ minWidth: { xs: '100%', lg: 100 } }} InputProps={{ sx: { borderRadius: 0 } }}>
            {[10, 25, 50, 100].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={0.75}>
            <Button variant="contained" size="small" startIcon={<FilterAltIcon />} onClick={applySearch} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Apply</Button>
            <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Clear</Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 1.5 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {sourceBreakdown.length === 0 ? <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>No income stream totals for this filter.</Typography> : sourceBreakdown.map((item) => (
            <Chip key={item.key} label={`${item.label}: ${money(item.amount, displayCurrency || defaultCurrency)} (${item.count})`} onClick={() => resetToFirstPage(() => setSourceFilter(item.key))} variant={sourceFilter === item.key ? 'filled' : 'outlined'} color={sourceFilter === item.key ? 'primary' : 'default'} sx={{ borderRadius: 0, fontWeight: 800 }} />
          ))}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, mb: 1.5, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.25, borderBottom: '0.5px solid', borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 900 } }}>
            <Tab value="ledger" label="Ledger" />
            <Tab value="queues" label="Queues" />
          </Tabs>
          {activeTab === 'ledger' && <Stack direction="row" spacing={1} alignItems="center">
            {transactions.length > 0 && <Button size="small" variant="outlined" onClick={() => toggleSelectAllVisible(!allVisibleSelected)} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>{allVisibleSelected ? 'Clear Page' : 'Select Page'}</Button>}
            {selectedTransactionIds.length > 0 && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 800 }}>{selectedTransactionIds.length} selected across ledger pages</Typography>}
            {selectedTransactionIds.length > 0 && <Button size="small" variant="outlined" onClick={() => setSelectedTransactionIds([])} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Clear</Button>}
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>New Entry</Button>
          </Stack>}
        </Stack>

        {activeTab === 'ledger' ? (
          <>
            <TableContainer sx={{ maxHeight: 720 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Tooltip title={allVisibleSelected ? 'Clear this page selection' : 'Select all rows on this page'}>
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          disabled={loading || visibleTransactionIds.length === 0}
                          onChange={(event) => toggleSelectAllVisible(event.target.checked)}
                          inputProps={{ 'aria-label': 'Select all ledger rows on this page' }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={labelSx}>Time</TableCell><TableCell sx={labelSx}>Income</TableCell><TableCell sx={labelSx}>User</TableCell><TableCell sx={labelSx}>Description / References</TableCell><TableCell align="right" sx={labelSx}>Amount</TableCell><TableCell align="right" sx={labelSx}>Status</TableCell><TableCell align="right" sx={labelSx}>Effect</TableCell><TableCell align="right" sx={labelSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 7 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : transactions.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 7 }}><ReceiptLongIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} /><Typography color="text.secondary">No transactions found</Typography></TableCell></TableRow>
                  ) : transactions.map((tx) => {
                    const parts = dateLabel(tx.date).split(' ');
                    const invoiceRecord = isInvoiceRecord(tx);
                    return (
                      <TableRow key={tx.id} hover selected={selectedTransactionIds.includes(tx.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={!invoiceRecord && selectedTransactionIds.includes(tx.id)}
                            disabled={invoiceRecord}
                            onChange={() => toggleTransactionSelection(tx.id)}
                            inputProps={{ 'aria-label': `Select ledger row ${tx.id}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography sx={{ fontSize: '0.76rem', fontWeight: 800 }}>{parts[0]}</Typography><Typography sx={{ ...monoSx, color: 'text.disabled' }}>{parts[1] || '--'}</Typography></TableCell>
                        <TableCell><Chip label={tx.incomeStreamLabel || 'Other'} size="small" variant="outlined" onClick={() => resetToFirstPage(() => setSourceFilter(tx.incomeStream || 'other'))} sx={{ borderRadius: 0, fontSize: '0.66rem', fontWeight: 900 }} />{!!tx.resourceTypes?.length && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.4, fontWeight: 800 }}>{tx.resourceTypes.slice(0, 3).map(prettyResourceType).join(', ')}</Typography>}</TableCell>
                        <TableCell><Typography sx={{ fontSize: '0.78rem', fontWeight: 900 }}>{tx.userEmail}</Typography><Typography sx={{ ...monoSx, color: 'text.disabled' }}>{tx.userHid || tx.userId}</Typography></TableCell>
                        <TableCell><Box display="flex" alignItems="flex-start" gap={1.2}><Box sx={{ mt: 0.35, color: 'text.secondary' }}>{isProvisionable(tx) ? <PriceCheckIcon fontSize="small" /> : <ReceiptLongIcon fontSize="small" />}</Box><Box sx={{ minWidth: 0 }}>{formatTransactionDescription(tx.displayDescription || tx.description)}<Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>{(tx.invoiceNumber || tx.invoiceId) && <Chip label={`Invoice ${tx.invoiceNumber || tx.invoiceId}`} size="small" variant="outlined" color="warning" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem' }} />}{tx.gateway && !invoiceRecord && <Chip label={tx.gateway} size="small" variant="outlined" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem' }} />}{tx.gatewayTxId && <Chip label={`Gateway ${tx.gatewayTxId}`} size="small" variant="outlined" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem' }} />}{tx.transactionId && <Chip label={`Txn ${tx.transactionId}`} size="small" variant="outlined" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem' }} />}</Stack></Box></Box></TableCell>
                        <TableCell align="right"><Typography sx={monoSx}>{money(tx.amount, tx.currency)}</Typography>{tx.originalCurrency && tx.originalCurrency !== tx.currency && <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>orig. {money(tx.originalAmount || 0, tx.originalCurrency)}</Typography>}</TableCell>
                        <TableCell align="right"><StatusChip status={tx.status} /></TableCell>
                        <TableCell align="right">{invoiceRecord ? <Chip label="Awaiting payment" color="warning" size="small" sx={{ borderRadius: 0, height: 22, fontSize: '0.66rem', fontWeight: 900 }} /> : <Chip label={tx.effectApplied ? 'Applied' : tx.processed ? 'Processed' : 'Open'} color={tx.effectApplied ? 'success' : tx.processed ? 'warning' : 'default'} size="small" sx={{ borderRadius: 0, height: 22, fontSize: '0.66rem', fontWeight: 900 }} />}</TableCell>
                        <TableCell align="right">{invoiceRecord ? <Tooltip title="Send invoice to client"><IconButton size="small" color="primary" onClick={() => { setInvoiceSendTarget(tx); setInvoiceSendReason(''); }} sx={{ borderRadius: 1 }}><MailOutlineIcon fontSize="small" /></IconButton></Tooltip> : <Stack direction="row" spacing={0.5} justifyContent="flex-end">{invoiceReferenceForTransaction(tx) && <Tooltip title="Send linked invoice to client"><IconButton size="small" color="primary" onClick={() => { setInvoiceSendTarget(tx); setInvoiceSendReason(''); }} sx={{ borderRadius: 1 }}><MailOutlineIcon fontSize="small" /></IconButton></Tooltip>}<IconButton size="small" onClick={() => openEdit(tx)} sx={{ borderRadius: 0 }}><EditIcon fontSize="small" /></IconButton><IconButton size="small" color="error" onClick={() => setDeleteTarget(tx)} sx={{ borderRadius: 0 }}><DeleteIcon fontSize="small" /></IconButton></Stack>}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && transactions.length > 0 && <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', borderTop: '0.5px solid', borderColor: 'divider' }}><Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" sx={{ '& .MuiPaginationItem-root': { borderRadius: 0 } }} /></Box>}
          </>
        ) : (
          <Box sx={{ p: 1.5 }}>
            {cancellationImpact && cancellationImpact.totals.count > 0 && <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 0 }}>Scheduled deletions: {cancellationImpact.totals.count} item(s), estimated lost monthly revenue {money(cancellationImpact.totals.monthlyLostRevenue, cancellationImpact.currency)}, offload {cancellationImpact.totals.channels} channel(s), {cancellationImpact.totals.outputs + cancellationImpact.totals.cpu + cancellationImpact.totals.hls + cancellationImpact.totals.hlsPlus} output slot(s).</Alert>}
            {(summary.failedCount > 0 || summary.pendingCount > 0) && <Alert severity={summary.failedCount > 0 ? 'error' : 'warning'} sx={{ mb: 1.5, borderRadius: 0 }}>Finance queue needs attention: {summary.failedCount} failed and {summary.pendingCount} pending financial item(s) in the current filter.</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
              <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}><Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}><WarningAmberIcon sx={{ color: summary.failedCount ? 'error.main' : 'text.secondary' }} /><Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>Failed / Pending Queue</Typography></Stack><Stack spacing={1}>{[...failedQueue, ...pendingQueue].length === 0 ? <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>No failed or pending financial entries in the visible rows.</Typography> : [...failedQueue, ...pendingQueue].map((tx) => <QueueRow key={tx.id} tx={tx} onOpen={() => resetToFirstPage(() => setStatusFilter(isFailed(tx.status) ? 'failed' : 'pending'))} />)}</Stack></Paper>
              <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}><Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}><AssignmentTurnedInIcon sx={{ color: 'text.secondary' }} /><Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>Provisioning Watch</Typography></Stack><Stack spacing={1}>{provisioningQueue.length === 0 ? <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>No paid resource purchases in the visible rows.</Typography> : provisioningQueue.map((tx) => <QueueRow key={tx.id} tx={tx} onOpen={() => resetToFirstPage(() => setSourceFilter(tx.incomeStream || 'all'))} />)}</Stack><Alert severity="info" sx={{ mt: 1.25, borderRadius: 0 }}>Paid resource purchases appear here for provisioning cross-check.</Alert></Paper>
            </Box>
          </Box>
        )}
      </Paper>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>{editingTx ? 'Edit Ledger Entry' : 'Create Ledger Entry'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {!editingTx && <TextField size="small" label="User email, ID, or hashid" value={form.userIdentifier || ''} onChange={(e) => setForm({ ...form, userIdentifier: e.target.value })} fullWidth />}
            {(
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0 }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 900 }}>Payment record</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    {editingTx
                      ? <TextField size="small" label="Gateway" value={editingTx.gateway || form.gateway || 'MANUAL'} fullWidth InputProps={{ readOnly: true }} />
                      : <TextField size="small" label="Gateway" value="Manual" fullWidth InputProps={{ readOnly: true }} />}
                    <TextField select size="small" label="Payment type" value={form.paymentType || ''} onChange={(e) => setForm({ ...form, paymentType: e.target.value })} fullWidth required>
                      <MenuItem value="" disabled>Select payment type</MenuItem>
                      {ledgerPaymentTypeOptions.map((type) => <MenuItem key={type} value={type}>{type.replace(/\b\w/g, (letter) => letter.toUpperCase())}</MenuItem>)}
                    </TextField>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField size="small" label="Reference number" value={form.gatewayTxId || ''} onChange={(e) => setForm({ ...form, gatewayTxId: e.target.value })} fullWidth required InputProps={editingTx ? { readOnly: true } : undefined} />
                    <TextField size="small" label="Number / account" value={form.paymentAccount || ''} onChange={(e) => setForm({ ...form, paymentAccount: e.target.value })} fullWidth required />
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField size="small" type="datetime-local" label="Payment date and time" value={form.paymentDateTime || ''} onChange={(e) => setForm({ ...form, paymentDateTime: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth required />
                    <TextField size="small" label="Day" value={form.paymentDateTime ? new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(new Date(form.paymentDateTime)) : ''} fullWidth InputProps={{ readOnly: true }} />
                  </Stack>
                </Stack>
              </Paper>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField size="small" type="number" label="Amount" value={form.amount ?? 0} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} fullWidth />
              <TextField size="small" label="Currency" value={form.currency || 'USD'} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} fullWidth />
              <TextField select size="small" label="Status" value={form.status || 'PENDING'} onChange={(e) => setForm({ ...form, status: e.target.value })} fullWidth>
                {['PENDING', 'PROCESSING', 'PAID', 'FAILED', 'CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED'].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              {editingTx
                ? <TextField size="small" label="Income stream" value={incomeStreams.find((stream) => stream.key === form.incomeStream)?.label || form.incomeStream || 'Other'} fullWidth InputProps={{ readOnly: true }} helperText={editingStorePurchase ? 'Derived from the purchased item and cannot be reclassified.' : 'Fixed when this ledger entry was created.'} />
                : <TextField select size="small" label="Income stream" value={form.incomeStream || 'manual'} onChange={(e) => setForm({ ...form, incomeStream: e.target.value })} fullWidth>{incomeStreams.filter((stream) => stream.key !== 'mixed').map((stream) => <MenuItem key={stream.key} value={stream.key}>{stream.label}</MenuItem>)}</TextField>}
            </Stack>
            <TextField size="small" label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <TextField size="small" label="Reason" value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} fullWidth required />
            {editingStorePurchase && <Alert severity="info">For confirmed EFT or manual funds, approve and provision to commission the exact resources purchased by the user.</Alert>}
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControlLabel control={<Switch checked={Boolean(form.processed)} onChange={(e) => setForm({ ...form, processed: e.target.checked })} />} label={editingStorePurchase ? 'Payment reviewed' : 'Processed'} />
              <FormControlLabel control={<Switch checked={Boolean(form.effectApplied)} onChange={(e) => setForm({ ...form, effectApplied: e.target.checked })} />} label={editingStorePurchase ? 'Provision purchased items' : 'Effect applied'} />
              {editingStorePurchase && <Button size="small" variant="outlined" startIcon={<PriceCheckIcon />} onClick={() => setForm({ ...form, status: 'PAID', processed: true, effectApplied: true })}>{editingTx?.effectApplied ? 'Verify & provision' : 'Approve & provision'}</Button>}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={saveForm} disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Save'}</Button></DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Ledger Entry</DialogTitle>
        <DialogContent><Stack spacing={1.5} sx={{ mt: 1 }}><Alert severity="error" sx={{ borderRadius: 0 }}>This removes the payment row and linked billing ledger/event rows.</Alert><Typography sx={{ fontSize: '0.8rem' }}>{deleteTarget?.userEmail} / {money(deleteTarget?.amount || 0, deleteTarget?.currency)}</Typography><TextField size="small" label="Reason" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} fullWidth required /></Stack></DialogContent>
        <DialogActions><Button onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</Button><Button color="error" variant="contained" onClick={confirmDelete} disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Delete'}</Button></DialogActions>
      </Dialog>

      <Dialog open={!!invoiceSendTarget} onClose={() => !invoiceSending && setInvoiceSendTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Send Invoice to Client</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 1 }}>A fresh PDF copy will be sent through the billing mailer. The invoice and its payment status will not be changed.</Alert>
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 900 }}>{invoiceSendTarget?.invoiceNumber || invoiceReferenceForTransaction(invoiceSendTarget) || 'Invoice'}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{invoiceSendTarget?.userEmail}</Typography>
              <Typography sx={monoSx}>{money(invoiceSendTarget?.amount || 0, invoiceSendTarget?.currency)}</Typography>
            </Box>
            <TextField size="small" label="Reason for sending" value={invoiceSendReason} onChange={(event) => setInvoiceSendReason(event.target.value)} inputProps={{ maxLength: 500 }} helperText={`${invoiceSendReason.length}/500 - recorded in the admin audit log`} fullWidth required multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setInvoiceSendTarget(null)} disabled={invoiceSending}>Cancel</Button><Button variant="contained" startIcon={invoiceSending ? <CircularProgress size={16} color="inherit" /> : <MailOutlineIcon />} onClick={confirmInvoiceSend} disabled={invoiceSending || !invoiceSendReason.trim()}>Send Invoice</Button></DialogActions>
      </Dialog>
    </Box>
  );
};
